/**
 * The level meter's scale (`R-HOST-11` — *microphone selected and working*).
 *
 * ## Decibels, because amplitude is wrong
 *
 * Do not draw the bar from amplitude, such as `min(1, rms * 4)`. A built-in laptop microphone at
 * speaking distance produces an RMS of roughly **0.005 to 0.03**, which that expression turns into
 * 2% to 12% of a small progress bar — **indistinguishable from zero.** The same arithmetic deciding
 * whether the microphone had been *heard* needs a threshold ordinary speech on such a microphone
 * may never sustain. So a meter that is working reads as a meter that is dead, and that is not a
 * cosmetic bug: it is the
 * pre-flight check reporting a working microphone as broken, and it is indivisible from the real
 * failure when all you have to look at is a static bar.
 *
 * Loudness is perceived logarithmically. The floor and ceiling here are chosen for what this is
 * *for* — not mastering audio, but answering *is it picking me up* — and it is a pure function so
 * that the answer can be pinned in `apps/host/test/meter.test.ts` rather than judged by eye.
 */

/** Below this is a quiet room's noise floor. */
export const FLOOR_DBFS = -60;
/** Headroom at the top, so a normal voice is not pinned at full scale. */
export const CEILING_DBFS = -6;
/**
 * *Heard*, and it is set **deliberately low**.
 *
 * A quiet room's noise floor sits near −66 dBFS and somebody sitting back from a laptop reaches
 * about −46, so the threshold has to live between the two — and nearer the floor than the voice.
 * A threshold at −45 dBFS would sit above soft speech and fail the check for a quietly-spoken
 * host: the same failure as the amplitude scale above, one step further along.
 *
 * The trade this accepts: a genuinely noisy room — a fan, a street — can tick this without anybody
 * speaking. That is the right way round. The check is advisory, since going live is gated on the
 * server (`R-HOST-11`, `packages/core/src/video/preflight.ts`), so a false *yes* costs a host
 * nothing and a false *no* tells them their working microphone is broken half an hour before a show.
 */
export const HEARD_DBFS = -55;

/** Amplitude to dBFS. `-Infinity` for true digital silence, which callers must expect. */
export function decibels(amplitude: number): number {
  return amplitude > 0 ? 20 * Math.log10(amplitude) : -Infinity;
}

/** An RMS amplitude as a 0-to-1 bar. */
export function meterScale(rms: number): number {
  const db = decibels(rms);
  if (!Number.isFinite(db)) return 0;
  return Math.max(0, Math.min(1, (db - FLOOR_DBFS) / (CEILING_DBFS - FLOOR_DBFS)));
}

/** Whether this level counts as the microphone having picked somebody up. */
export function audible(rms: number): boolean {
  return decibels(rms) > HEARD_DBFS;
}
